import { ModelEntityType } from '@upright/common/model/input/types';
import type { PageType } from './types';

export function errorPageState(status: number | undefined, generic = false, signedOut = false, pageType?: PageType) {
  if (status === 401 || status === 403) {
    // A signed-out visitor is usually denied only because they have not logged in yet, so the way
    // forward is logging in, not asking an administrator.
    if (signedOut) {
      return {
        title: 'You don’t have access to this page',
        description: 'Log in to see this page if you have an Upright account.',
        retry: false,
        loginHref: '/login',
      };
    }
    return {
      title: 'You don’t have access to this page',
      description: 'Contact your organization administrator if you need access.',
      retry: false,
    };
  }
  if (generic || (status !== undefined && status !== 404 && status !== 410)) {
    return {
      title: 'We couldn’t load this page',
      description: 'Please try again. If the problem continues, contact us.',
      retry: true,
    };
  }
  // Naming the entity reveals nothing the status did not: a 404 or 410 already says the entity does
  // not exist, and a viewer who cannot read one gets a 401/403, answered above.
  const missingEntity = pageType === undefined ? undefined : MISSING_ENTITY_STATES[pageType];
  return {
    ...(missingEntity ?? {
      title: 'Page not found',
      description: 'The link may be incorrect, or this page may no longer be available.',
    }),
    retry: false,
  };
}

const MISSING_ENTITY_STATES: Partial<Record<PageType, { title: string; description: string }>> = {
  [ModelEntityType.COMPANY]: {
    title: 'Company not found',
    description: 'Check the link, or return home to find another company.',
  },
  [ModelEntityType.PORTFOLIO]: {
    title: 'Portfolio not found',
    description: 'Check the link, or return home to see your portfolios.',
  },
};
